import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import DocuVaultUploadModal from '../../DocuVault/DocuVaultUploadModal';
import DocuVaultAuthModal from '../../DocuVault/DocuVaultAuthModal';
import DocuVaultDocumentCard from '../../DocuVault/DocuVaultDocumentCard';
import DocuVaultAccessDelegationModal from '../../DocuVault/DocuVaultAccessDelegationModal';
import DocuVaultBeneficiaryChecks from '../../DocuVault/DocuVaultBeneficiaryChecks';
import DocuVaultEmergencyReleaseModal from '../../DocuVault/DocuVaultEmergencyReleaseModal';
import {
  fetchDocuments, deleteDocument, formatFileSize,
  DOCUMENT_CATEGORIES, countExpiryAlerts, sortByExpiryUrgency,
} from '../../../services/docuvaultService';
import { getSession } from '../../../api';

const {
  FiX, FiShield, FiLock, FiZap, FiUploadCloud, FiSearch,
  FiFilter, FiRefreshCw, FiCheckCircle, FiUsers, FiFileText,
  FiFolder, FiAlertTriangle, FiEye, FiUserCheck, FiClipboard,
} = FiIcons;

const DocumentVaultModal = ({ isOpen, onClose }) => {
  const [docs, setDocs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searchQ, setSearchQ]        = useState('');
  const [filterCat, setFilterCat]   = useState('all');
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [authDoc, setAuthDoc]       = useState(null);
  const [viewDoc, setViewDoc]       = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [isDelegationOpen, setDelegationOpen] = useState(false);
  const [isBeneficiaryChecksOpen, setBeneficiaryChecksOpen] = useState(false);
  const [emergencyDoc, setEmergencyDoc] = useState(null);
  const user = getSession();

  const loadDocs = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await fetchDocuments({ category: filterCat });
      setDocs(data);
    } catch { setDocs([]); }
    setLoading(false);
  }, [filterCat, user]);

  useEffect(() => { if (isOpen) loadDocs(); }, [isOpen, loadDocs]);

  const handleDelete = async (id, s3Key) => {
    if (!window.confirm('Permanently delete this document from the vault?')) return;
    setDeleting(id);
    await deleteDocument(id, s3Key);
    setDocs(d => d.filter(doc => doc.id !== id));
    setDeleting(null);
  };

  const filteredDocs = docs.filter(d =>
    d.original_name?.toLowerCase().includes(searchQ.toLowerCase()) ||
    d.description?.toLowerCase().includes(searchQ.toLowerCase())
  );

  // Expiry alerts & urgency sorting
  const expiryCounts = countExpiryAlerts(docs);
  const hasExpiryAlerts = expiryCounts.expired > 0 || expiryCounts.critical > 0 || expiryCounts.warning > 0;
  const displayDocs = sortByExpiryUrgency(filteredDocs);

  // Stats
  const totalSize    = docs.reduce((acc, d) => acc + (d.file_size || 0), 0);
  const privateCount = docs.filter(d => d.access_level === 'private').length;
  const multiCount   = docs.filter(d => d.authorization_required === 1).length;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white rounded-[40px] shadow-2xl w-full max-w-4xl overflow-hidden border border-brand-dark flex flex-col max-h-[94vh]">

          {/* Header */}
          <div className="p-8 border-b border-brand-light flex items-center justify-between bg-gradient-to-r from-accent-navy via-[#1a2a5e] to-purple-900 flex-shrink-0 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
            <div className="flex items-center gap-5 relative z-10">
              <div className="relative">
                <div className="w-14 h-14 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl">
                  <SafeIcon icon={FiShield} className="w-7 h-7" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full flex items-center justify-center border-2 border-white">
                  <SafeIcon icon={FiLock} className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-0.5">
                  <h3 className="text-2xl font-serif text-white">DocuVault™</h3>
                  <span className="px-2 py-0.5 bg-accent-gold text-accent-navy text-[8px] font-black uppercase tracking-widest rounded-lg">
                    AWS S3 · AES-256
                  </span>
                </div>
                <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                  Secure Legal Document Repository · Multi-Party Authorization
                </p>
              </div>
            </div>
            <div className="relative z-10 flex items-center gap-3">
              <button
                onClick={() => setBeneficiaryChecksOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all text-[10px] font-black uppercase tracking-widest"
                title="Review beneficiary details"
              >
                <SafeIcon icon={FiClipboard} className="w-4 h-4" />
                Beneficiary Checks
              </button>
              <button
                onClick={() => setDelegationOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all text-[10px] font-black uppercase tracking-widest"
                title="Manage access delegation"
              >
                <SafeIcon icon={FiUserCheck} className="w-4 h-4" />
                Manage Access
              </button>
              <button onClick={loadDocs} className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all">
                <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={onClose} className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-4 divide-x divide-brand-light border-b border-brand-light flex-shrink-0">
            {[
              { label: 'Total Documents', value: docs.length, icon: FiFileText, color: 'text-blue-500' },
              { label: 'Storage Used', value: formatFileSize(totalSize), icon: FiFolder, color: 'text-accent-gold' },
              { label: 'Private Docs', value: privateCount, icon: FiLock, color: 'text-accent-navy' },
              hasExpiryAlerts
                ? { label: 'Expiring Soon', value: expiryCounts.critical + expiryCounts.warning, icon: FiAlertTriangle, color: 'text-red-500' }
                : { label: 'Multi-Party Auth', value: multiCount, icon: FiUsers, color: 'text-purple-500' },
            ].map(s => (
              <div key={s.label} className="p-4 text-center">
                <SafeIcon icon={s.icon} className={`w-5 h-5 ${s.color} mx-auto mb-1`} />
                <p className="text-lg font-serif font-bold text-accent-navy">{s.value}</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="p-5 border-b border-brand-light flex items-center gap-3 flex-wrap flex-shrink-0 bg-brand-light/30">
            <div className="relative flex-1 min-w-[200px]">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search documents..."
                className="w-full pl-11 pr-4 py-2.5 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto">
              {DOCUMENT_CATEGORIES.slice(0, 5).map(cat => (
                <button key={cat.id} onClick={() => setFilterCat(cat.id)}
                  className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${
                    filterCat === cat.id ? 'bg-accent-navy text-white' : 'bg-white border border-brand-dark text-text-muted hover:border-accent-gold'
                  }`}>
                  {cat.label}
                </button>
              ))}
            </div>

            <button onClick={() => setUploadOpen(true)}
              className="flex items-center gap-2 bg-accent-gold text-white px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/20 flex-shrink-0">
              <SafeIcon icon={FiUploadCloud} className="w-4 h-4" /> Upload Document
            </button>
          </div>

          {/* Document List */}
          <div className="flex-1 overflow-y-auto p-6">
            {hasExpiryAlerts && (
              <div className={`mb-4 rounded-2xl border p-4 flex items-start gap-3 ${
                expiryCounts.expired > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
              }`}>
                <SafeIcon icon={FiAlertTriangle} className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  expiryCounts.expired > 0 ? 'text-red-500' : 'text-amber-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-accent-navy">
                    {expiryCounts.expired > 0
                      ? `${expiryCounts.expired} document${expiryCounts.expired > 1 ? 's' : ''} expired`
                      : expiryCounts.critical > 0
                        ? `${expiryCounts.critical} document${expiryCounts.critical > 1 ? 's' : ''} expiring within 3 days`
                        : `${expiryCounts.warning} document${expiryCounts.warning > 1 ? 's' : ''} expiring within 14 days`
                    }
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    Review and renew these documents to maintain uninterrupted access for authorized parties.
                  </p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-sm text-text-muted font-light">Loading your vault...</p>
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-brand-light rounded-3xl flex items-center justify-center mb-6">
                  <SafeIcon icon={FiShield} className="w-10 h-10 text-text-muted/30" />
                </div>
                <h4 className="text-xl font-serif text-accent-navy mb-2">
                  {searchQ ? 'No documents match your search' : 'Your vault is empty'}
                </h4>
                <p className="text-sm text-text-muted font-light mb-6 max-w-xs">
                  {searchQ ? 'Try a different search term.' : 'Upload your first legal document to get started. All files are encrypted with AES-256 and stored securely on AWS S3.'}
                </p>
                {!searchQ && (
                  <button onClick={() => setUploadOpen(true)}
                    className="flex items-center gap-2 bg-accent-navy text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
                    <SafeIcon icon={FiUploadCloud} className="w-4 h-4" /> Upload First Document
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AnimatePresence>
                  {displayDocs.map((doc, i) => (
                    <DocuVaultDocumentCard
                      key={doc.id}
                      doc={doc}
                      index={i}
                      onDelete={handleDelete}
                      onAuthorize={setAuthDoc}
                      onView={setViewDoc}
                      onEmergencyRelease={setEmergencyDoc}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Security Footer */}
          <div className="p-4 border-t border-brand-light bg-brand-light/50 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4 flex-wrap">
              {[
                { icon: FiShield, label: 'AES-256 Encrypted', color: 'text-green-600' },
                { icon: FiLock, label: 'AWS S3 Storage', color: 'text-blue-600' },
                { icon: FiUsers, label: 'Multi-Party Auth', color: 'text-purple-600' },
                { icon: FiZap, label: 'SHA-256 Checksums', color: 'text-accent-gold' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-text-muted">
                  <SafeIcon icon={b.icon} className={`w-3.5 h-3.5 ${b.color}`} />
                  {b.label}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5 text-[9px] font-black text-green-600 uppercase tracking-widest">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Vault Secured
            </div>
          </div>
        </motion.div>
      </div>

      {/* View Doc Modal (simple) */}
      <AnimatePresence>
        {viewDoc && (
          <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg p-8 border border-brand-dark">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-serif text-accent-navy">Document Details</h4>
                <button onClick={() => setViewDoc(null)} className="p-2 text-text-muted hover:text-accent-navy rounded-xl">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4 text-sm">
                {[
                  { label: 'File Name', value: viewDoc.original_name },
                  { label: 'Category', value: DOCUMENT_CATEGORIES.find(c => c.id === viewDoc.file_category)?.label },
                  { label: 'File Size', value: formatFileSize(viewDoc.file_size) },
                  { label: 'File Type', value: viewDoc.file_type },
                  { label: 'S3 Key', value: viewDoc.s3_key, mono: true },
                  { label: 'Uploaded', value: new Date(viewDoc.created_at).toLocaleString() },
                  { label: 'Encryption', value: viewDoc.encryption_status },
                ].map(row => (
                  <div key={row.label} className="flex justify-between gap-4 border-b border-brand-light pb-3 last:border-0">
                    <span className="text-[10px] font-black uppercase tracking-widest text-text-muted flex-shrink-0">{row.label}</span>
                    <span className={`font-bold text-accent-navy text-right truncate ${row.mono ? 'font-mono text-xs' : ''}`}>{row.value || '—'}</span>
                  </div>
                ))}
              </div>
              {viewDoc.description && (
                <div className="mt-4 p-4 bg-brand-light rounded-2xl">
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-1">Description</p>
                  <p className="text-sm text-accent-navy font-light">{viewDoc.description}</p>
                </div>
              )}
              <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-xl flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                <p className="text-xs text-green-700 font-bold">This document is encrypted and stored securely on AWS S3.</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <DocuVaultUploadModal
        isOpen={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => { setUploadOpen(false); loadDocs(); }}
      />
      <DocuVaultAuthModal
        isOpen={!!authDoc}
        document={authDoc}
        onClose={() => setAuthDoc(null)}
        onSuccess={loadDocs}
      />
      <DocuVaultAccessDelegationModal
        isOpen={isDelegationOpen}
        onClose={() => setDelegationOpen(false)}
        onUpdate={loadDocs}
      />
      <DocuVaultBeneficiaryChecks
        isOpen={isBeneficiaryChecksOpen}
        documents={docs}
        onClose={() => setBeneficiaryChecksOpen(false)}
        onUpdated={loadDocs}
      />
      <DocuVaultEmergencyReleaseModal
        isOpen={!!emergencyDoc}
        document={emergencyDoc}
        onClose={() => setEmergencyDoc(null)}
        onSuccess={() => { setEmergencyDoc(null); loadDocs(); }}
      />
    </>
  );
};

export default DocumentVaultModal;